<template>
  <div class="settings-view">
    <NavBar />

    <!-- Popup PIN -->
    <div class="modal-overlay" v-if="!pinValide">
      <div class="modal">
        <div class="modal-icon">🔐</div>
        <p class="eyebrow">Espace protégé</p>
        <h3>Accès sécurisé</h3>
        <p class="modal-copy">Saisissez votre PIN pour accéder aux paramètres.</p>
        <input
          v-model="pinSaisi"
          class="input pin-input"
          type="password"
          placeholder="• • • •"
          maxlength="4"
          @keyup.enter="verifierPin"
          ref="pinInput"
        />
        <p v-if="erreurPin" class="error">{{ erreurPin }}</p>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="$router.push('/dashboard')">← Retour</button>
          <button class="btn btn-primary" @click="verifierPin" :disabled="loadingPin || pinSaisi.length !== 4">
            {{ loadingPin ? 'Vérification...' : 'Confirmer' }}
          </button>
        </div>
      </div>
    </div>

    <div class="page" v-if="pinValide">
      <header class="settings-hero">
        <div>
          <p class="eyebrow">FIDmarket</p>
          <h2 class="page-title">Paramètres</h2>
          <p class="page-subtitle">Pilotez le profil commerçant, la sécurité et les règles de fidélité.</p>
        </div>
      </header>

      <!-- Profil -->
      <section class="card settings-card">
        <div class="section-header">
          <div>
            <p class="section-kicker">Profil commerçant</p>
            <h3>Identité de l'enseigne</h3>
          </div>
        </div>

        <div class="form-grid single">
          <div class="form-group">
            <label class="label">Nom de l'enseigne</label>
            <input v-model="profil.nom_enseigne" class="input" placeholder="Ex: Marché Herrera" />
          </div>
        </div>

        <div class="subsection-title">Modification du PIN de connexion</div>
        <div class="form-grid three">
          <div class="form-group">
            <label class="label">Ancien PIN</label>
            <input v-model="profil.ancien_pin" class="input" type="password" inputmode="numeric" placeholder="4 chiffres" maxlength="4" />
          </div>
          <div class="form-group">
            <label class="label">Nouveau PIN</label>
            <input v-model="profil.nouveau_pin" class="input" type="password" inputmode="numeric" placeholder="4 chiffres" maxlength="4" />
          </div>
          <div class="form-group">
            <label class="label">Confirmer le PIN</label>
            <input v-model="profil.confirmer_pin" class="input" type="password" inputmode="numeric" placeholder="4 chiffres" maxlength="4" />
          </div>
        </div>

        <p v-if="erreurProfil" class="message error">{{ erreurProfil }}</p>
        <p v-if="succesProfil" class="message success">Profil mis à jour ✓</p>

        <button class="btn btn-primary btn-fit" @click="sauvegarderProfil" :disabled="loadingProfil">
          {{ loadingProfil ? 'Sauvegarde...' : 'Sauvegarder le profil' }}
        </button>
      </section>

      <!-- Règles de fidélité -->
      <div v-if="chargement" class="card loading-card">
        <p>Chargement des paramètres...</p>
      </div>

      <section class="card settings-card" v-else>
        <div class="section-header">
          <div>
            <p class="section-kicker">Règles fidélité</p>
            <h3>Points et récompenses</h3>
          </div>
        </div>

        <div class="form-grid two">
          <div class="form-group">
            <label class="label">Points par euro dépensé</label>
            <input v-model.number="form.points_par_euro" class="input" type="number" min="1" />
            <p class="hint">Ex: 10 → 1€ = 10 points</p>
          </div>
          <div class="form-group">
            <label class="label">Points offerts à l'inscription</label>
            <input v-model.number="form.points_bienvenue" class="input" type="number" min="0" />
          </div>
        </div>

        <div class="subsection-title">Récompense</div>
        <div class="form-grid three">
          <div class="form-group">
            <label class="label">Seuil de points pour réduction</label>
            <input v-model.number="form.seuil_points" class="input" type="number" min="1" />
          </div>
          <div class="form-group">
            <label class="label">Type de réduction</label>
            <select v-model="form.type_reduction" class="input">
              <option value="montant_fixe">Montant fixe (€)</option>
              <option value="pourcentage">Pourcentage (%)</option>
            </select>
          </div>
          <div class="form-group">
            <label class="label">Valeur de la réduction</label>
            <input v-model.number="form.valeur_reduction" class="input" type="number" min="0" step="0.5" />
            <p class="hint">{{ form.type_reduction === 'montant_fixe' ? 'En euros (€)' : 'En pourcentage (%)' }}</p>
          </div>
        </div>

        <div class="form-grid two">
          <div class="form-group">
            <label class="label">Expiration des points (jours)</label>
            <input v-model.number="form.expiration_jours" class="input" type="number" min="0" placeholder="365" />
            <p class="hint">0 ou vide = points sans expiration</p>
          </div>
          <div class="form-group">
            <label class="label">Durée de vie des bons d'achat (jours)</label>
            <input v-model.number="form.duree_bon_jours" class="input" type="number" min="1" placeholder="90" />
            <p class="hint">Ex: 90 = le bon expire 90 jours après sa création</p>
          </div>
        </div>

        <!-- Aperçu -->
        <div class="apercu">
          <h4>Aperçu</h4>
          <p>Pour 1€ dépensé → <strong>{{ form.points_par_euro }} points</strong></p>
          <p>Après <strong>{{ form.seuil_points }} points</strong> → réduction de
            <strong>{{ form.valeur_reduction }}{{ form.type_reduction === 'montant_fixe' ? '€' : '%' }}</strong>
          </p>
          <p>Il faut dépenser <strong>{{ montantPourReduction }} €</strong> pour obtenir une réduction</p>
        </div>

        <p v-if="erreur" class="message error">{{ erreur }}</p>
        <p v-if="succes" class="message success">Paramètres sauvegardés ✓</p>

        <button class="btn btn-primary btn-fit" @click="sauvegarder" :disabled="loading">
          {{ loading ? 'Sauvegarde...' : 'Sauvegarder les paramètres' }}
        </button>
      </section>

      <!-- PIN paramètres -->
      <section class="card settings-card" v-if="pinValide">
        <div class="section-header">
          <div>
            <p class="section-kicker">Sécurité / PIN</p>
            <h3>PIN paramètres</h3>
            <p class="section-copy">
              {{ pinParametresDefini ? 'Un PIN secondaire protège cet espace. Différent de votre PIN de connexion.' : 'Actuellement : votre PIN de connexion donne accès aux paramètres.' }}
            </p>
          </div>
        </div>

        <div class="pin-param-status" :class="pinParametresDefini ? 'status-active' : 'status-inactive'">
          <span>{{ pinParametresDefini ? 'PIN paramètres actif' : 'PIN paramètres non défini' }}</span>
          <button v-if="pinParametresDefini" class="btn-supprimer-pin" @click="showSupprimerPin = !showSupprimerPin">
            Supprimer
          </button>
        </div>

        <div class="form-grid three spaced">
          <div class="form-group">
            <label class="label">{{ pinParametresDefini ? 'PIN actuel' : 'PIN de connexion actuel' }}</label>
            <input v-model="pinParam.actuel" class="input" type="password" inputmode="numeric" placeholder="4 chiffres" maxlength="4" />
          </div>
          <div class="form-group">
            <label class="label">Nouveau PIN paramètres</label>
            <input v-model="pinParam.nouveau" class="input" type="password" inputmode="numeric" placeholder="4 chiffres" maxlength="4" />
          </div>
          <div class="form-group">
            <label class="label">Confirmer</label>
            <input v-model="pinParam.confirmer" class="input" type="password" inputmode="numeric" placeholder="4 chiffres" maxlength="4" />
          </div>
        </div>

        <p v-if="erreurPinParam" class="message error">{{ erreurPinParam }}</p>
        <p v-if="succesPinParam" class="message success">{{ succesPinParam }}</p>

        <button class="btn btn-primary btn-fit"
          @click="sauvegarderPinParametres"
          :disabled="loadingPinParam || pinParam.nouveau.length !== 4">
          {{ loadingPinParam ? 'Sauvegarde...' : 'Définir ce PIN' }}
        </button>

        <!-- Supprimer PIN -->
        <div v-if="showSupprimerPin" class="supprimer-pin-box">
          <p class="hint">Saisissez votre PIN paramètres actuel pour le supprimer :</p>
          <div class="delete-pin-row">
            <input v-model="pinSupprimer" class="input pin-delete-input" type="password" inputmode="numeric" placeholder="PIN actuel" maxlength="4" />
            <button class="btn btn-danger" @click="supprimerPinParametres" :disabled="loadingPinParam">
              Supprimer
            </button>
          </div>
        </div>
      </section>

      <!-- Accessibilité -->
      <section class="card settings-card" v-if="pinValide">
        <div class="section-header compact">
          <div>
            <p class="section-kicker">Accessibilité</p>
            <h3>Confort d'affichage</h3>
          </div>
        </div>
        <div class="contraste-row">
          <div class="contraste-info">
            <strong>Mode contraste fort</strong>
            <p class="hint">Améliore la lisibilité en plein soleil — textes plus grands, couleurs plus marquées</p>
          </div>
          <button
            class="toggle-btn"
            :class="{ active: contrasteActif }"
            @click="toggleContraste"
          >
            <span class="toggle-track">
              <span class="toggle-thumb"></span>
            </span>
            <span class="toggle-label">{{ contrasteActif ? 'Activé' : 'Désactivé' }}</span>
          </button>
        </div>
        <div class="contraste-preview" v-if="contrasteActif">
          ☀️ Mode contraste fort activé — navigation optimisée pour le plein soleil
        </div>
      </section>

    </div>
  </div>
</template>

<script>
import NavBar from '../components/NavBar.vue'
import api    from '../api'

export default {
  name: 'ParametresView',
  components: { NavBar },
  data() {
    const commercant = JSON.parse(localStorage.getItem('commercant') || '{}')
    return {
      // PIN accès
      pinValide:   false,
      pinSaisi:    '',
      erreurPin:   '',
      loadingPin:  false,

      // Profil
      profil: {
        nom_enseigne:  commercant.nom_enseigne || '',
        ancien_pin:    '',
        nouveau_pin:   '',
        confirmer_pin: ''
      },
      erreurProfil:  '',
      succesProfil:  false,
      loadingProfil: false,

      // Règles
      chargement: true,
      form: {
        points_par_euro:  1,
        seuil_points:     100,
        type_reduction:   'montant_fixe',
        valeur_reduction: 5,
        points_bienvenue: 20,
        expiration_jours: 365,
        duree_bon_jours:  90
      },
      erreur:  '',
      succes:  false,
      loading: false,

      // Accessibilite
      contrasteActif: localStorage.getItem('contraste_fort') === '1',
      pinParametresDefini: false,
      pinParam: { actuel: '', nouveau: '', confirmer: '' },
      erreurPinParam: '',
      succesPinParam: '',
      loadingPinParam: false,
      showSupprimerPin: false,
      pinSupprimer: ''
    }
  },
  computed: {
    montantPourReduction() {
      if (!this.form.points_par_euro) return '—'
      return (this.form.seuil_points / this.form.points_par_euro).toFixed(2)
    }
  },
  methods: {
    async verifierPin() {
      this.erreurPin  = ''
      this.loadingPin = true
      try {
        const res = await api.post('/auth/verifier-pin-parametres', { pin: this.pinSaisi })
        this.pinParametresDefini = res.data?.pin_parametres_defini || false
        this.pinValide = true
        this.pinSaisi  = ''
      } catch (err) {
        this.erreurPin = err.response ? 'PIN incorrect' : 'Erreur de connexion'
        this.pinSaisi  = ''
      } finally {
        this.loadingPin = false
      }
    },

    async sauvegarderProfil() {
      this.erreurProfil  = ''
      this.succesProfil  = false
      this.loadingProfil = true

      if (this.profil.nouveau_pin) {
        if (!this.profil.ancien_pin) {
          this.erreurProfil = 'Saisissez votre ancien PIN'
          this.loadingProfil = false
          return
        }
        if (this.profil.nouveau_pin !== this.profil.confirmer_pin) {
          this.erreurProfil = 'Les PINs ne correspondent pas'
          this.loadingProfil = false
          return
        }
        if (String(this.profil.nouveau_pin).length !== 4) {
          this.erreurProfil = 'Le PIN doit faire 4 chiffres'
          this.loadingProfil = false
          return
        }
      }

      try {
        const payload = { nom_enseigne: this.profil.nom_enseigne }
        if (this.profil.nouveau_pin) {
          payload.ancien_pin  = this.profil.ancien_pin
          payload.nouveau_pin = this.profil.nouveau_pin
        }
        await api.patch('/auth/profil', payload)

        const commercant = JSON.parse(localStorage.getItem('commercant') || '{}')
        commercant.nom_enseigne = this.profil.nom_enseigne
        localStorage.setItem('commercant', JSON.stringify(commercant))

        this.profil.ancien_pin    = ''
        this.profil.nouveau_pin   = ''
        this.profil.confirmer_pin = ''
        this.succesProfil = true
        setTimeout(() => this.succesProfil = false, 3000)
      } catch (err) {
        this.erreurProfil = err.response?.data?.error || 'Erreur lors de la sauvegarde'
      } finally {
        this.loadingProfil = false
      }
    },

    async verifierPinParametresDefini() {
      try {
        const res = await api.post('/auth/verifier-pin-parametres', { pin: '0000' })
        this.pinParametresDefini = res.data?.pin_parametres_defini || false
      } catch (err) {
        this.pinParametresDefini = err.response?.data?.pin_parametres_defini || false
      }
    },
    async sauvegarderPinParametres() {
      this.erreurPinParam = ''
      this.succesPinParam = ''
      if (this.pinParam.nouveau !== this.pinParam.confirmer) {
        this.erreurPinParam = 'Les PINs ne correspondent pas'
        return
      }
      this.loadingPinParam = true
      try {
        await api.post('/auth/pin-parametres', {
          pin_actuel:  this.pinParam.actuel,
          nouveau_pin: this.pinParam.nouveau
        })
        this.succesPinParam = 'PIN paramètres sauvegardé'
        this.pinParametresDefini = true
        this.pinParam = { actuel: '', nouveau: '', confirmer: '' }
        setTimeout(() => this.succesPinParam = '', 3000)
      } catch (err) {
        this.erreurPinParam = err.response?.data?.error || 'Erreur'
      } finally {
        this.loadingPinParam = false
      }
    },
    async supprimerPinParametres() {
      this.loadingPinParam = true
      try {
        await api.delete('/auth/pin-parametres', { data: { pin_actuel: this.pinSupprimer } })
        this.pinParametresDefini = false
        this.showSupprimerPin = false
        this.pinSupprimer = ''
        this.succesPinParam = 'PIN paramètres supprimé'
        setTimeout(() => this.succesPinParam = '', 3000)
      } catch (err) {
        this.erreurPinParam = err.response?.data?.error || 'Erreur'
      } finally {
        this.loadingPinParam = false
      }
    },
    toggleContraste() {
      this.contrasteActif = !this.contrasteActif
      if (this.contrasteActif) {
        localStorage.setItem('contraste_fort', '1')
        document.body.classList.add('contraste-fort')
      } else {
        localStorage.setItem('contraste_fort', '0')
        document.body.classList.remove('contraste-fort')
      }
    },
    async charger() {
      this.chargement = true
      try {
        const res = await api.get('/auth/regles')
        if (res.data) {
          this.form.points_par_euro  = res.data.points_par_euro
          this.form.seuil_points     = res.data.seuil_points
          this.form.type_reduction   = res.data.type_reduction
          this.form.valeur_reduction = res.data.valeur_reduction
          this.form.points_bienvenue = res.data.points_bienvenue
          this.form.expiration_jours = res.data.expiration_jours
          this.form.duree_bon_jours  = res.data.duree_bon_jours || 90
        }
      } catch (err) {
        console.error(err)
      } finally {
        this.chargement = false
      }
    },

    async sauvegarder() {
      this.erreur  = ''
      this.succes  = false
      this.loading = true
      try {
        await api.post('/auth/regles', this.form)
        this.succes = true
        setTimeout(() => this.succes = false, 3000)
      } catch (err) {
        this.erreur = err.response?.data?.error || 'Erreur lors de la sauvegarde'
      } finally {
        this.loading = false
      }
    }
  },
  mounted() {
    this.charger()
    this.$nextTick(() => { this.$refs.pinInput?.focus() })
  }
}
</script>

<style scoped>
.settings-view {
  min-height: 100vh;
  background:
    radial-gradient(circle at 12% 0%, rgba(45, 106, 79, 0.08), transparent 30%),
    linear-gradient(180deg, #f7faf8 0%, #eef4f1 100%);
}

.modal-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: rgba(15, 23, 42, 0.58);
  backdrop-filter: blur(8px);
  z-index: 200;
}

.modal {
  width: 100%;
  max-width: 390px;
  padding: 32px;
  text-align: center;
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.22);
  border-radius: 18px;
  box-shadow: 0 28px 70px rgba(15, 23, 42, 0.24);
}

.modal-icon {
  display: grid;
  place-items: center;
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  background: #edf7f1;
  border-radius: 18px;
  font-size: 30px;
}

.eyebrow,
.section-kicker {
  margin: 0 0 8px;
  color: #2d6a4f;
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.modal h3 {
  margin: 0 0 8px;
  color: #14213d;
  font-size: 24px;
  font-weight: 800;
}

.modal-copy {
  margin: 0 0 18px;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
}

.modal-actions {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 16px;
}

.page {
  max-width: 1080px;
  margin: 0 auto;
  padding: 28px 20px 44px;
}

.settings-hero {
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-bottom: 22px;
  padding: 8px 2px;
}

.page-title {
  margin: 0;
  color: #111827;
  font-size: 34px;
  font-weight: 850;
}

.page-subtitle {
  max-width: 620px;
  margin: 8px 0 0;
  color: #64748b;
  font-size: 15px;
  line-height: 1.6;
}

.card {
  background: #ffffff;
  border: 1px solid rgba(148, 163, 184, 0.2);
  border-radius: 18px;
  box-shadow: 0 18px 45px rgba(15, 23, 42, 0.07);
}

.settings-card {
  margin-bottom: 18px;
  padding: 24px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  gap: 18px;
  margin-bottom: 20px;
  padding-bottom: 18px;
  border-bottom: 1px solid #eef2f7;
}

.section-header.compact {
  margin-bottom: 8px;
}

.section-header h3 {
  margin: 0;
  color: #172033;
  font-size: 20px;
  font-weight: 820;
}

.section-copy {
  margin: 8px 0 0;
  color: #64748b;
  font-size: 14px;
  line-height: 1.5;
}

.subsection-title {
  margin: 20px 0 12px;
  color: #334155;
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
}

.form-grid {
  display: grid;
  gap: 16px;
}

.form-grid.single {
  grid-template-columns: minmax(0, 1fr);
}

.form-grid.two {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.form-grid.three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.form-grid.spaced {
  margin-top: 16px;
}

.form-group {
  min-width: 0;
}

.label {
  display: block;
  margin-bottom: 8px;
  color: #475569;
  font-size: 13px;
  font-weight: 750;
}

.input {
  width: 100%;
  min-height: 48px;
  padding: 12px 14px;
  color: #111827;
  background: #fbfdff;
  border: 1.5px solid #dbe4ee;
  border-radius: 14px;
  outline: none;
  box-sizing: border-box;
  font-size: 15px;
  transition: border-color 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.input:focus {
  background: #ffffff;
  border-color: #2d6a4f;
  box-shadow: 0 0 0 4px rgba(45, 106, 79, 0.12);
}

.pin-input {
  min-height: 58px;
  font-size: 28px;
  font-weight: 800;
  text-align: center;
  letter-spacing: 8px;
}

.hint {
  margin: 7px 0 0;
  color: #7c8a9a;
  font-size: 12px;
  line-height: 1.45;
}

.loading-card {
  margin-bottom: 18px;
  padding: 42px;
  text-align: center;
  color: #64748b;
}

.apercu {
  margin-top: 18px;
  padding: 18px;
  background: linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%);
  border: 1px solid #bbf7d0;
  border-radius: 16px;
}

.apercu h4 {
  margin: 0 0 10px;
  color: #166534;
  font-size: 14px;
  font-weight: 850;
}

.apercu p {
  margin: 5px 0;
  color: #334155;
  font-size: 14px;
  line-height: 1.45;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 12px 20px;
  border: none;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.16s ease, box-shadow 0.16s ease, background 0.16s ease;
}

.btn:hover:not(:disabled) {
  transform: translateY(-1px);
}

.btn-fit {
  width: auto;
  margin-top: 18px;
}

.btn-primary {
  color: #ffffff;
  background: linear-gradient(135deg, #2d6a4f 0%, #1b4332 100%);
  box-shadow: 0 12px 24px rgba(45, 106, 79, 0.22);
}

.btn-secondary {
  color: #334155;
  background: #f1f5f9;
}

.btn-danger {
  color: #ffffff;
  background: #dc2626;
  box-shadow: 0 10px 20px rgba(220, 38, 38, 0.18);
}

.btn:disabled,
.btn-primary:disabled {
  background: #cbd5e1;
  box-shadow: none;
  cursor: not-allowed;
}

.message,
.error,
.success {
  margin: 14px 0 0;
  padding: 11px 13px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 750;
}

.error {
  color: #b91c1c;
  background: #fef2f2;
  border: 1px solid #fecaca;
}

.success {
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.contraste-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
  padding-top: 8px;
}

.contraste-info {
  flex: 1;
  min-width: 220px;
}

.contraste-info strong {
  display: block;
  margin-bottom: 4px;
  color: #172033;
  font-size: 15px;
  font-weight: 850;
}

.contraste-preview {
  margin-top: 14px;
  padding: 12px 14px;
  color: #ffff00;
  background: #111111;
  border-radius: 14px;
  font-size: 13px;
  font-weight: 800;
}

.toggle-btn {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  min-height: 48px;
  padding: 8px 10px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 999px;
  cursor: pointer;
  flex-shrink: 0;
}

.toggle-track {
  position: relative;
  display: block;
  width: 54px;
  height: 30px;
  background: #cbd5e1;
  border-radius: 999px;
  transition: background 0.25s ease;
}

.toggle-btn.active .toggle-track {
  background: #2d6a4f;
}

.toggle-thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  display: block;
  width: 24px;
  height: 24px;
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 2px 8px rgba(15, 23, 42, 0.22);
  transition: transform 0.25s ease;
}

.toggle-btn.active .toggle-thumb {
  transform: translateX(24px);
}

.toggle-label {
  min-width: 66px;
  color: #475569;
  font-size: 13px;
  font-weight: 800;
}

.pin-param-status {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 13px 15px;
  border-radius: 14px;
  font-size: 14px;
  font-weight: 850;
}

.status-active {
  color: #166534;
  background: #f0fdf4;
  border: 1px solid #bbf7d0;
}

.status-inactive {
  color: #475569;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
}

.btn-supprimer-pin {
  min-height: 38px;
  padding: 8px 12px;
  color: #b91c1c;
  background: #fff1f2;
  border: 1px solid #fecdd3;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 800;
  cursor: pointer;
}

.supprimer-pin-box {
  margin-top: 16px;
  padding: 16px;
  background: #fff7f7;
  border: 1px solid #fecaca;
  border-radius: 16px;
}

.delete-pin-row {
  display: flex;
  gap: 10px;
  margin-top: 10px;
}

.pin-delete-input {
  max-width: 160px;
}

@media (max-width: 820px) {
  .form-grid.two,
  .form-grid.three {
    grid-template-columns: 1fr;
  }

  .page-title {
    font-size: 30px;
  }
}

@media (max-width: 600px) {
  .page {
    padding: 18px 12px 96px;
  }

  .settings-card,
  .modal {
    padding: 20px;
  }

  .settings-hero {
    margin-bottom: 14px;
  }

  .page-title {
    font-size: 28px;
  }

  .modal-actions,
  .delete-pin-row {
    grid-template-columns: 1fr;
    display: grid;
  }

  .btn,
  .btn-fit {
    width: 100%;
    min-height: 52px;
  }

  .contraste-row {
    align-items: stretch;
    flex-direction: column;
  }

  .toggle-btn {
    justify-content: space-between;
    width: 100%;
  }

  .pin-param-status {
    align-items: stretch;
    flex-direction: column;
  }

  .pin-delete-input {
    max-width: none;
  }
}

:global(body.contraste-fort) .settings-view {
  background: #000000;
}

:global(body.contraste-fort) .settings-card,
:global(body.contraste-fort) .modal {
  border-color: #ffff00;
}

:global(body.contraste-fort) .section-header {
  border-bottom-color: #ffff00;
}

:global(body.contraste-fort) .section-kicker,
:global(body.contraste-fort) .eyebrow,
:global(body.contraste-fort) .section-copy,
:global(body.contraste-fort) .toggle-label {
  color: #ffff00;
}
</style>
